import { StepperWidgetTypeEnum } from '@msc-b2c/interfaces/enums';
import {
  countDaysUntil,
  getEnableWciOnBookingConfirmation,
  getWciClosesOn,
  replacePlaceholders,
} from '@msc-b2c/shared-utils';
import { getBalanceDueDate, isWithinCheckInWindow } from '../../utils/checkinUtils';
import { Step } from '@msc-b2c/interfaces/types';
import { getDepartureDate } from '../../utils/myAreaUtils';

/**
 * Processes a step object and determines its active and disabled labels, as well as its disabled status,
 * based on the provided step context and step type.
 *
 * @param {Step} step - The step object to process.
 * @param stepContext - The context object containing information about the step's state.
 * @returns - The processed step object with updated labels and disabled status.
 */
export function operateOnSteps(step: Step, stepContext) {
  const {
    isCheckInDoneForAll,
    isBookingConfirmed,
    isBookingOptional,
    isCabinAssigned,
    checkinStart,
    booking,
  } = stepContext;
  const stepActiveLabel = step.stepActiveLabel;
  let stepDisabledLabel = step.stepDisabledLabel;
  const stepWciCurrentlyOpenedLabel = step.stepDisabledWciCurrentlyOpenedLabel;
  let isDisabled = true;

  // based on the type of step, understand if it should be disabled or not and if there are placeholders to replace
  switch (step.type) {
    case StepperWidgetTypeEnum.WCI_STATUS: {
      isDisabled = !isCheckInDoneForAll;

      // Calculate days until cruise departure
      const departureDate = getDepartureDate(booking);
      const remainingDays = countDaysUntil(new Date().toISOString(), departureDate);
      const hour = String(getWciClosesOn() ? (getWciClosesOn() ?? 2) * 24 : 0);
      const day = String(getWciClosesOn());
      if (
        !isCheckInDoneForAll &&
        (getEnableWciOnBookingConfirmation() || isWithinCheckInWindow(remainingDays))
      ) {
        stepDisabledLabel = replacePlaceholders(stepWciCurrentlyOpenedLabel, {
          hour,
          day,
        });
      } else {
        stepDisabledLabel = replacePlaceholders(stepDisabledLabel, {
          hour,
          Day: checkinStart.day,
          Month: checkinStart.month,
          Year: checkinStart.year,
        });
      }
      break;
    }

    case StepperWidgetTypeEnum.BOOKING_STATUS:
      isDisabled = !isBookingConfirmed && isBookingOptional;
      break;

    case StepperWidgetTypeEnum.STATEROOM_STATUS:
      isDisabled = !isCabinAssigned;
      break;

    case StepperWidgetTypeEnum.PAYMENT_STATUS: {
      // if the booking is optional, we need to check if the payment is done
      const totalWithoutPrepaid = booking?.totalWithoutPrepaid?.due;
      const totalWithPrepaidDueDate = booking?.totalWithPrepaid?.dueDate;
      const total = booking?.total?.due;
      const totalDueDate = booking?.total?.dueDate;

      const totalPrice =
        Number(totalWithoutPrepaid?.gross) > 0
          ? Number(totalWithoutPrepaid?.gross)
          : Number(total?.gross);

      isDisabled = totalPrice > 0;

      // get the due date based on the total amount to be paid
      const dueDateToUse =
        Number(totalWithoutPrepaid?.dueDate) > 0 ? totalWithPrepaidDueDate : totalDueDate;
      const balanceDueDate = getBalanceDueDate(dueDateToUse);

      stepDisabledLabel = replacePlaceholders(stepDisabledLabel, {
        Day: balanceDueDate.day,
        Month: balanceDueDate.month,
        Year: balanceDueDate.year,
      });
      break;
    }

    default:
      break;
  }

  return {
    ...step,
    stepActiveLabel,
    stepDisabledLabel,
    isDisabled,
  };
}

/**
 * Sorts an array of steps based on their `isDisabled` property.
 *
 * @param {Step[]} newSteps - An array of step objects to be sorted.
 * @returns {Step[]} - The sorted array of steps, with ENABLED steps APPEARING BEFORE disabled ones.
 */
export function getSortedSteps(newSteps: Step[]): Step[] {
  return newSteps.sort((step1, step2) => Number(step1.isDisabled) - Number(step2.isDisabled));
}
